import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import { $generateReceiptByIdStore, $paymentByIdStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { AmountUtils, ColorUtils, DateUtils, RoutePaths } from '@shared/utils';
import { Button, ColorsEnum, ExchangeRate, Icon, NoData, PageContainer, Spinner, Text } from '@shared/widgets';
import TransactionLogoIcon from '@assets/icons/transactions/transaction-logo.svg';
import GobblerIcon from '@assets/icons/transactions/gobbler.svg';
import DocumentIcon from '@assets/icons/document.svg';

const splitCamelCase = (str: string) => str.replace(/([A-Z])/g, ' $1').trim();

export const Transaction = () => {
  const params = useParams<{ tranId: string }>();
  const { navigate } = useRouteHook();

  const paymentStore = useUnit($paymentByIdStore.store);

  useEffect(() => {
    $paymentByIdStore.events.effect(params.tranId);

    return () => {
      $paymentByIdStore.events.clear();
    };
  }, []);

  useEffect(() => {
    if (paymentStore.loading) return;
    if (!paymentStore.error) return;
    $paymentByIdStore.events.clear();
    navigate(RoutePaths.error.onNavigate());
  }, [paymentStore.loading, paymentStore.error]);

  const download = () => $generateReceiptByIdStore.events.effect(params.tranId);

  const color = ColorUtils.getDepositPaymentStatusStyles(paymentStore.data?.status);
  return (
    <PageContainer backText='Back to all accounts' title='Transaction'>
      {paymentStore.loading ? (
        <Spinner />
      ) : !paymentStore.data ? (
        <NoData />
      ) : (
        <div className='flex flex-col gap-6 p-6 bg-white dark:bg-soft-black'>
          <div className='flex w-full'>
            <div className='w-20 h-20 rounded-full bg-primary flex items-center justify-center'>
              <TransactionLogoIcon />
            </div>
            <div className='flex flex-col ml-5'>
              <div className='flex'>
                <Text size='3xl'>{AmountUtils.transformToAmountWithCurrency(paymentStore.data.totalAmount, paymentStore.data.currency)}</Text>
                <div className={clsx('flex p-1 rounded-sm ml-3', color.bg, color.text)}>
                  <p className='my-auto'>{color.label}</p>
                </div>
              </div>
              <Text size='sm'>Fee: {AmountUtils.transformToAmountWithCurrency(paymentStore.data.fee, paymentStore.data.currency)}</Text>
              <div className='flex gap-2'>
                <Text size='sm'>
                  {DateUtils.formatDate(paymentStore.data.date, {
                    withTime: true,
                    adjustTime: true,
                  })}
                </Text>
                <Text size='sm' color={ColorsEnum.Secondary}>
                  {splitCamelCase(paymentStore.data.typeOfTransfer)}
                </Text>
              </div>
              {/* <div className='flex w-fit bg-ice py-1 px-2 mt-3'>
                <div className='w-5 h-5'>
                  <TicketIcon />
                </div>
                <p className='text-sm text-soft-black dark:text-primary mx-2'>Account Transaction Details</p>
                <div className='flex h-3 w-3'>
                  <EditIcon />
                </div>
              </div> */}
            </div>
          </div>
          {/* TODO don't remove that */}
          <div className='flex justify-between gap-3 w-full'>
            <Button color='primary' onClick={download} className='h-12 py-3 w-full' fullWidth>
              <Icon icon={<DocumentIcon />} color='inherit' />
              Download
            </Button>
            {/* <Button color='primary' className='h-12 py-3' fullWidth>
              <SupportIcon /> Support
            </Button>
            <Button color='primary' className='h-12 py-3' fullWidth>
              <EditIcon /> Edit note
            </Button> */}
          </div>
          <hr />
          <div className='flex'>
            <div className='flex flex-col w-fit'>
              <Text color={ColorsEnum.Secondary}>Transaction account</Text>
              <div className='flex mt-3'>
                <Icon
                  icon={<GobblerIcon />}
                  className='flex items-center h-11 px-5 bg-soft-black border border-dark-white rounded-sm'
                  color='white'
                />
                <div className='ml-3 flex flex-col justify-center'>
                  <Text>{paymentStore.data.accountNumber}</Text>
                  <Text size='xs' color={ColorsEnum.Gray} isUppercase>
                    {paymentStore.data.accountType}
                  </Text>
                </div>
              </div>
            </div>
            {/* <div className='flex flex-col w-full ml-14'>
              <p className='text-slate dark:text-gray'>Rewards</p>
              <div className='flex mt-3'>
                <div className=''>
                  <RewardIcon />
                </div>
                <div className='flex flex-col ml-3 justify-center'>
                  <p className='text-soft-black'>+300 Reward points</p>
                </div>
              </div>
            </div> */}
          </div>
          <div className='flex flex-col'>
            <Text color={ColorsEnum.Secondary}>Transaction details</Text>
            <div className='flex gap-20 mt-3'>
              <div className='flex flex-col gap-2'>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Type of Transaction</Text>
                  <Text weight='semibold' size='sm'>
                    {splitCamelCase(paymentStore.data.typeOfTransfer)}
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Sender Name</Text>
                  <Text weight='semibold' size='sm'>
                    {paymentStore.data.senderName}
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Sender IBAN/Account</Text>
                  <Text weight='semibold' size='sm'>
                    {paymentStore.data.senderIban}
                  </Text>
                </div>
                {paymentStore.data.beneficiaryName && (
                  <div className='flex flex-col'>
                    <Text color={ColorsEnum.Secondary}>Beneficiary Name</Text>
                    <Text weight='semibold' size='sm'>
                      {paymentStore.data.beneficiaryName}
                    </Text>
                  </div>
                )}
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Beneficiary IBAN/Account</Text>
                  <Text weight='semibold' size='sm'>
                    {paymentStore.data.beneficiaryIban}
                  </Text>
                </div>
                {/* <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Email</Text>
                  <Text weight='semibold' size='sm'>
                    {paymentStore.data.currency}
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Country</Text>
                  <Text weight='semibold' size='sm'>
                    {paymentStore.data.currency}
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Reference</Text>
                  <Text weight='semibold' size='sm'>
                    {paymentStore.data.currency}
                  </Text>
                </div> */}
              </div>
              <div className='w-1/3 flex flex-col gap-2'>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Sender Currency</Text>
                  <Text weight='semibold' size='sm'>
                    {paymentStore.data.currencyIn}
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Beneficiary Currency</Text>
                  <Text weight='semibold' size='sm'>
                    {paymentStore.data.currencyOut}
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Exchange rate</Text>
                  <Text weight='semibold' size='sm' className='flex gap-2'>
                    {AmountUtils.transformToAmountWithCurrency(1, paymentStore.data.currencyIn)} ={' '}
                    {AmountUtils.transformToAmountWithCurrency(paymentStore.data.exchangeRate, paymentStore.data.currencyOut)}
                    <ExchangeRate />
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Sender Amount</Text>
                  <Text weight='semibold' size='sm'>
                    {AmountUtils.transformToAmountWithCurrency(paymentStore.data.senderAmount, paymentStore.data.currencyIn)}
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Beneficiary Amount</Text>
                  <Text weight='semibold' size='sm'>
                    {AmountUtils.transformToAmountWithCurrency(paymentStore.data.beneficiaryAmount, paymentStore.data.currencyOut)}
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Fee</Text>
                  <Text weight='semibold' size='sm'>
                    {AmountUtils.transformToAmountWithCurrency(paymentStore.data.fee, paymentStore.data.currency)}
                  </Text>
                </div>
                <div className='flex flex-col'>
                  <Text color={ColorsEnum.Secondary}>Actual Amount</Text>
                  <Text weight='semibold' size='sm'>
                    {AmountUtils.transformToAmountWithCurrency(paymentStore.data.totalAmount, paymentStore.data.currency)}
                  </Text>
                </div>
              </div>
              <div className='w-1/3 flex flex-col gap-2'>
                <div className='flex flex-col'>
                  <p className='text-slate dark:text-gray'>Purpose</p>
                  <p className='text-sm font-semibold text-soft-black dark:text-white'>{paymentStore.data.purpose || '-'}</p>
                </div>
                <div className='flex flex-col'>
                  <p className='text-slate dark:text-gray'>Fast Payment</p>
                  <p className='text-sm font-semibold text-soft-black dark:text-white'>{paymentStore.data.isFast ? 'Yes' : 'No'}</p>
                </div>
                <div className='flex flex-col'>
                  <p className='text-slate dark:text-gray'>Description</p>
                  <p className='text-sm font-semibold text-soft-black dark:text-white'>{paymentStore.data.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};
